import { useMemo } from 'react';
import duratilesRaw from '../data/duratiles.json';
import BrandCatalog from '../components/catalog/BrandCatalog';
import type { CatalogItem, BrandConfig } from '../components/catalog/BrandCatalog';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildBrandCatalogSchema, buildBreadcrumbSchema } from '@/lib/seo/schemas';

/* ── Raw type from JSON ── */
interface DuratilesProduct {
  id: string;
  title: string;
  seriesName: string;
  collection: string;
  sku: string;
  image: string;
  images: string[];
  size: string;
  color: string;
  surface: string;
  thickness: string;
  usageArea: string;
  technology: string;
  waterAbsorption: string;
  frostResistance: string;
  slipResistance: string;
  chemicalResistance: string;
  boxM2: number | string;
  boxPcs: number | string;
  bodyType: string;
  badge: string;
  features: string[];
  pdfUrl: string;
  descriptionClean: string;
}

/* ── Brand config ── */
const duratilesBrand: BrandConfig = {
  brandName: 'Duratiles Karo',
  tagline: 'Seramik & Porselen — Akgün Group',
  accentColor: '#7B5E3A',
  accentLight: '#f5f0eb',
  accentMid: '#e8ddd0',
  groupMode: 'flat',
  searchPlaceholder: 'Seri adı, renk veya ebat ara…',
  filterGroups: [
    { label: 'Koleksiyon', field: 'collection' },
    { label: 'Ebat', field: 'size' },
    { label: 'Yüzey', field: 'surface' },
    { label: 'Kullanım Alanı', field: 'usageArea' },
    { label: 'Gövde Tipi', field: 'bodyType' },
  ],
};

/* ── Map raw → CatalogItem ── */
function mapToItem(p: DuratilesProduct): CatalogItem {
  const specs: Record<string, string | number> = {};
  if (p.size)               specs['Ebat']             = p.size;
  if (p.color)              specs['Renk / Ton']        = p.color;
  if (p.surface)            specs['Yüzey']             = p.surface;
  if (p.collection)         specs['Koleksiyon']        = p.collection;
  if (p.usageArea)          specs['Kullanım Alanı']    = p.usageArea;
  if (p.technology)         specs['Teknoloji']         = p.technology;
  if (p.thickness)          specs['Kalınlık']          = p.thickness;
  if (p.bodyType)           specs['Gövde Tipi']        = p.bodyType;
  if (p.boxM2)              specs['Kasa / m²']         = p.boxM2 + ' m²';
  if (p.boxPcs)             specs['Kasa / Adet']       = String(p.boxPcs);
  if (p.waterAbsorption)    specs['Su Emme']           = p.waterAbsorption;
  if (p.frostResistance)    specs['Don Direnci']       = p.frostResistance;
  if (p.slipResistance)     specs['Kaymazlık']         = p.slipResistance;
  if (p.chemicalResistance) specs['Kimyasal Direnç']   = p.chemicalResistance;

  return {
    id: p.id,
    title: p.title,
    sku: p.sku,
    images: [p.image, ...(p.images || [])].filter(Boolean),
    groupKey: p.collection || p.seriesName || p.title,
    groupLabel: p.collection,
    subtitle: [p.color, p.size].filter(Boolean).join(' · '),
    chipFilters: {
      Koleksiyon: p.collection || '',
      Ebat: p.size || '',
      Yüzey: (p.surface || '').split(',').map(s => s.trim()).filter(Boolean),
      'Kullanım Alanı': (p.usageArea || '').split(' & ').map(s => s.trim()).filter(Boolean),
      'Gövde Tipi': p.bodyType || '',
    },
    specs,
    features: p.features || [],
    description: p.descriptionClean,
    badge: p.badge || undefined,
    pdfUrl: p.pdfUrl || undefined,
  };
}

export default function Duratiles() {
  const products = duratilesRaw as DuratilesProduct[];
  const items = useMemo<CatalogItem[]>(() => products.map(mapToItem), [products]);

  useSEO({
    title: 'Duratiles Seramik Diyarbakır | Porselen Karo Kataloğu — Mayi Mimarlık',
    description:
      "Diyarbakır Duratiles ana bayi Mayi Mimarlık. 60x120, 30x180 porselen karo, ahşap görünümlü seramik, yer ve duvar karosu. Kayapınar showroom — ücretsiz teklif.",
    keywords: localKeywords(
      'duratiles diyarbakır',
      'seramik diyarbakır',
      'porselen karo diyarbakır',
      'yer karosu diyarbakır',
      '60x120 seramik diyarbakır',
      'ahşap seramik diyarbakır'
    ),
    canonical: 'https://mayimimarlik.com/duratiles',
  });

  const schema = useMemo(() => {
    const catalog = buildBrandCatalogSchema({
      brandName: 'Duratiles',
      description: 'Duratiles seramik ve porselen karo — Diyarbakır ana bayi kataloğu',
      url: 'https://mayimimarlik.com/duratiles',
      productCount: items.length,
    });
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'Duratiles Seramik', url: 'https://mayimimarlik.com/duratiles' },
    ]);
    const { '@context': _a, ...cat } = catalog;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [cat, crumb] };
  }, [items.length]);

  useStructuredData('schema-duratiles', schema);

  return <BrandCatalog config={duratilesBrand} items={items} />;
}
